import * as React from "react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Form primitives replacing the Bootstrap `form-row` + `form-group` + `col-*` +
 * `form-control` combination that the dashboard forms repeat hundreds of times.
 *
 * Every field there is the same four lines: a label with an optional required
 * marker, a control, and an error message underneath. `Field` owns that shape so
 * the screens stop re-implementing it, and reuses the field styling already
 * validated on the sign-in screen.
 *
 * Built for react-hook-form v6, whose registration is a ref: pass it as
 * `inputRef={register({ ... })}`.
 */

/** 12-column grid; children pick a width with `span`. */
const FormGrid = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("grid grid-cols-12 gap-x-4 gap-y-4", className)}
    {...props}
  />
));
FormGrid.displayName = "FormGrid";

// Written out rather than interpolated so Tailwind's scanner can see them.
const SPANS = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

/** Column width for one grid item; shared with the other grid-aware controls. */
const gridSpan = (span) => SPANS[span] || SPANS[12];

const FIELD_SKIN =
  "h-11 rounded-lg border-transparent bg-muted/60 text-sm shadow-none " +
  "placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:bg-background " +
  "focus-visible:ring-4 focus-visible:ring-primary/15 focus-visible:ring-offset-0 " +
  "disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed";

/**
 * One labelled control.
 *
 * `as` picks the element: "input" (default), "textarea" or "select". Anything
 * else can be passed as `children`, which replaces the control entirely while
 * keeping the label and error layout.
 *
 * `icon` puts a glyph inside the control, replacing Bootstrap's
 * `input-group-prepend`, which rendered it as a separate boxed cell alongside.
 */
const Field = ({
  id,
  name,
  label,
  hint,
  required = false,
  error,
  inputRef,
  span = 12,
  as,
  icon,
  className,
  children,
  ...props
}) => {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  // Without `as`, children replace the control entirely — that is how the
  // shared react-select pickers get a label and an error slot. With `as`, they
  // are the control's own content: the options of a `<select>`.
  const element = as ?? (children ? "custom" : "input");

  const control = (
    element === "custom" ? (
      children
    ) : element === "textarea" ? (
      <textarea
        id={id}
        name={name}
        ref={inputRef}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className={cn(
          FIELD_SKIN,
          "flex min-h-[5rem] w-full px-3 py-2 transition-[border-color,box-shadow]",
          "aria-[invalid=true]:border-destructive",
          className
        )}
        {...props}
      />
    ) : element === "select" ? (
      <select
        id={id}
        name={name}
        ref={inputRef}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className={cn(
          FIELD_SKIN,
          "flex w-full px-3 transition-[border-color,box-shadow]",
          "aria-[invalid=true]:border-destructive",
          className
        )}
        {...props}
      >
        {children}
      </select>
    ) : (
      <Input
        id={id}
        name={name}
        ref={inputRef}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className={cn(FIELD_SKIN, icon && "pl-10", className)}
        {...props}
      />
    )
  );

  return (
    <div className={cn("col-span-12 space-y-1.5", gridSpan(span))}>
      {label && (
        <Label htmlFor={id} className="text-[13px] text-muted-foreground">
          {label}
          {required && (
            <span className="ml-0.5 text-destructive" aria-hidden="true">
              *
            </span>
          )}
        </Label>
      )}

      {icon && element !== "custom" ? (
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          >
            {icon}
          </span>
          {control}
        </div>
      ) : (
        control
      )}

      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}

      {error && (
        <p id={`${id}-error`} className="text-xs font-medium text-destructive">
          {error.message}
        </p>
      )}
    </div>
  );
};

export { FormGrid, Field, FIELD_SKIN, gridSpan };
